import React, { createContext, ReactNode, useCallback, useContext, useState } from 'react'

// Define the allowed keys for the button registry
type ButtonKey = 'puzzleRating' | 'rewardsNavigation' // Add more keys as needed

// Define the type for the button registry
type ButtonRegistry = {
    [key in ButtonKey]?: ReactNode
}

// Define the context type
interface HeaderButtonsContextType {
    buttons: ReactNode[]
    registerButton: (key: ButtonKey, button: ReactNode) => void
    unregisterButton: (key: ButtonKey) => void
}

// Create the context
const HeaderButtonsContext = createContext<HeaderButtonsContextType | undefined>(undefined)

// Provider component
export const HeaderButtonsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [buttonRegistry, setButtonRegistry] = useState<ButtonRegistry>({})

    const registerButton = useCallback((key: ButtonKey, button: ReactNode) => {
        setButtonRegistry((prev) => ({ [key]: button, ...prev }))
    }, [])

    const unregisterButton = useCallback((key: ButtonKey) => {
        setButtonRegistry((prev) => {
            const { [key]: _, ...rest } = prev
            return rest
        })
    }, [])

    const buttons = Object.values(buttonRegistry)

    return (
        <HeaderButtonsContext.Provider value={{ buttons, registerButton, unregisterButton }}>
            {children}
        </HeaderButtonsContext.Provider>
    )
}

// Hook to use the context
export const useHeaderButtons = (): HeaderButtonsContextType => {
    const context = useContext(HeaderButtonsContext)
    if (!context) {
        throw new Error('useHeaderButtons must be used within a HeaderButtonsProvider')
    }
    return context
}
